<template>
	<ion-menu
		ref="menu"
		content-id="main-content"
		type="overlay"
		:side="menuSide"
	>
		<ion-header>
			<ion-toolbar color="branding">
				<SideMenuDropdown @button-clicked="closeMenu" />
			</ion-toolbar>
		</ion-header>

		<ion-content color="branding">
			<ion-list id="menu-list">
				<side-menu-item
					v-for="page in pages"
					:key="page.title"
					:page="page"
					@menu-item-clicked="handleMenuItemClick"
				/>
			</ion-list>
		</ion-content>

		<ion-footer>
			<ion-item color="branding">
				<LanguageSelector
					:visible="showLanguageSelector"
					:user-locale="myUser?.locale"
				/>
			</ion-item>
			<ion-toolbar color="branding">
				<QuickCreateMenu />
			</ion-toolbar>
		</ion-footer>
	</ion-menu>
</template>

<script setup lang="ts">
	import { computed, ref, watch } from "vue";
	import {
		IonContent,
		IonList,
		IonFooter,
		IonToolbar,
		IonMenu,
		IonItem,
		IonHeader,
	} from "@ionic/vue";
	import {
		mapOutline,
		checkboxOutline,
		calendarClearOutline,
		layersOutline,
		listOutline,
		readerOutline,
		trendingUpOutline,
		pieChartOutline,
	} from "ionicons/icons";
	import { useI18n } from "vue-i18n";
	import { useMyUser } from "seedgreen-shared/queries/user";
	import { useFeatureFlags } from "seedgreen-shared/queries/featureFlags";
	import { FEATURE_FLAGS } from "seedgreen-shared/types/featureFlags.types";
	import { useDeviceSize } from "@/composables/useDeviceSize";
	import SideMenuDropdown from "@/views/SideMenu/SideMenuDropdown.vue";
	import SideMenuItem from "@/views/SideMenu/SideMenuItem.vue";
	import LanguageSelector from "@/components/ui/LanguageSelector.vue";
	import QuickCreateMenu from "@/views/SideMenu/QuickCreateMenu.vue";

	export interface MenuItem {
		title: string;
		url?: string;
		iosIcon?: string;
		children?: {
			title: string;
			url?: string;
			iosIcon?: string;
			tag?: string;
			hidden?: boolean;
		}[];
		hidden?: boolean;
		tag?: string;
	}

	const { myUser } = useMyUser();
	const { featureFlags } = useFeatureFlags();

	const hideHarvest = computed<boolean>(() => {
		if (!myUser.value || !myUser.value.id || !featureFlags.value) {
			return true; // Hide option while logged out
		}

		if (featureFlags.value.includes(FEATURE_FLAGS.StrawberryHarvestTools)) {
			return false; // Show option if feature flag is enabled
		}

		return true;
	});

	const hideCalendar = computed<boolean>(() => {
		if (!myUser.value || !myUser.value.id || !featureFlags.value) {
			return true;
		}

		if (featureFlags.value.includes(FEATURE_FLAGS.BetaCalendar)) {
			return false;
		}
		return true;
	});

	const menu = ref<InstanceType<typeof IonMenu>>();
	const { isDesktop } = useDeviceSize();
	const menuSide = computed(() => (isDesktop.value ? "start" : "end"));
	const showLanguageSelector = ref(false);
	const { t } = useI18n();

	const pages = computed<MenuItem[]>(() =>
		[
			{
				title: t("sidemenu.lot-search"),
				url: "/lot-search",
				iosIcon: mapOutline,
			},
			{
				title: t("sidemenu.orders"),
				url: "/orders",
				iosIcon: listOutline,
				hidden: true,
			},
			{
				title: t("sidemenu.planning"),
				url: "/planning",
				iosIcon: layersOutline,
				hidden: true,
			},
			{
				title: t("sidemenu.tasks"),
				url: "/tasks",
				iosIcon: checkboxOutline,
			},
			// {
			// 	title: t("sidemenu.notes"),
			// 	url: "/notes",
			// 	iosIcon: documentTextOutline,
			// },
			{
				title: t("sidemenu.scheduling"),
				url: "/calendar",
				iosIcon: calendarClearOutline,
				hidden: hideCalendar.value,
			},
			{
				title: t("sidemenu.harvest"),
				iosIcon: trendingUpOutline,
				hidden: hideHarvest.value,
				children: [
					{
						title: "Seasons",
						url: "/harvest/seasons",
						hidden:
							!myUser.value?.internalUser && !myUser.value?.admin,
					},
					{
						title: t("sidemenu.harvest-berries"),
						url: "/harvest/berries/overview",
					},
				],
			},
			{
				title: t("sidemenu.resources"),
				url: "/resources",
				iosIcon: readerOutline,
				hidden: true,
			},
			{
				title: t("sidemenu.analytics"),
				url: "/analytics",
				iosIcon: pieChartOutline,
				hidden: true,
			},
			// Later: group under "Analytics"
			{
				title: t("sidemenu.reports"),
				url: "/reports",
				iosIcon: pieChartOutline,
				tag: "Beta",
			},
		].filter((page) => !page.hidden),
	);

	const { locale, availableLocales } = useI18n();

	watch(myUser, () => {
		const userLocale = myUser.value?.locale;
		if (userLocale) {
			showLanguageSelector.value = true;
			if (availableLocales.includes(userLocale)) {
				locale.value = userLocale;
			}
		}
	});

	function closeMenu() {
		if (!isDesktop.value) {
			menu.value?.$el.close();
		}
	}

	function handleMenuItemClick() {
		closeMenu();
	}
</script>
